import classNames from 'classnames'
import { FC } from 'react'
import { AiToolIcon } from 'src/components/AiToolIcon'
import {
  BrandDashboard,
  CitationHealth,
  CitationType,
} from 'src/services/aiListeningApi/brandProfile.types'
import { DeltaBadge } from './DeltaBadge'
import styles from './dashboard.module.scss'
import { MeterBar } from '../charts/MeterBar'
import { CITATION_HEALTH_TONES, CITATION_TYPE_TONES, Pill } from '../Pill'
import {
  bandOf,
  CITATION_HEALTH_LABELS,
  CITATION_TYPE_LABELS,
  displayUrl,
  firstLetter,
  formatNumber,
  formatPercent,
} from '../utils'

const HEALTH_COLORS: Record<CitationHealth, string> = {
  low: '#ff6833',
  moderate: '#c7ced1',
  strong: '#8080ff',
}

const TYPE_COLORS: Record<CitationType, string> = {
  competitor: '#ff6833',
  owned: '#8080ff',
  third_party: '#c7ced1',
}

const EmptyRows: FC<{ text?: string }> = ({
  text = 'No data for this period',
}) => <p className={styles.emptyRows}>{text}</p>

// ---------- Mentions by platform ----------

interface PlatformsCardProps {
  needingAttention: number
  platforms: BrandDashboard['platforms']
}

export const PlatformsCard: FC<PlatformsCardProps> = ({
  needingAttention,
  platforms,
}) => {
  const maxShare = Math.max(...platforms.map((platform) => platform.share), 0)

  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h2 className={styles.cardTitle}>Total AI Mentions by Platform</h2>
          <p className={styles.cardSubtitle}>Bar colour = citation health</p>
        </div>
        {needingAttention > 0 && (
          <Pill dot size="md" tone="orange">
            {needingAttention}{' '}
            {needingAttention === 1
              ? 'platform needs attention'
              : 'platforms need attention'}
          </Pill>
        )}
      </div>

      <div className={classNames(styles.gridHead, styles.platformGrid)}>
        <span>AI Model</span>
        <span>Share of mentions</span>
        <span className={styles.alignRight}>Mentions</span>
        <span className={styles.alignRight}>Share</span>
        <span>Citations</span>
      </div>
      {platforms.length === 0 && <EmptyRows />}
      <div className={styles.platformRows}>
        {platforms.map((platform) => (
          <div
            className={classNames(styles.platformGrid, styles.platformRow, {
              [styles.platformRowAlert]: platform.citationHealth === 'low',
            })}
            key={platform.aiTool}
          >
            <span className={styles.platformName}>
              <AiToolIcon
                className={styles.platformIcon}
                name={platform.aiTool}
              />
              {platform.aiTool}
            </span>
            <MeterBar
              color={
                platform.citationHealth
                  ? HEALTH_COLORS[platform.citationHealth]
                  : undefined
              }
              value={maxShare ? (platform.share / maxShare) * 100 : 0}
            />
            <strong className={styles.alignRight}>
              {formatNumber(platform.mentions)}
            </strong>
            <span className={classNames(styles.muted, styles.alignRight)}>
              {formatPercent(platform.share)}
            </span>
            <span>
              {platform.citationHealth && (
                <Pill dot tone={CITATION_HEALTH_TONES[platform.citationHealth]}>
                  {CITATION_HEALTH_LABELS[platform.citationHealth]}
                </Pill>
              )}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

// ---------- Top topics ----------

export const TopicsCard: FC<{ topics: BrandDashboard['topics'] }> = ({
  topics,
}) => (
  <section className={styles.card}>
    <div className={styles.cardHeader}>
      <div>
        <h2 className={styles.cardTitle}>Top 5 Topics</h2>
        <p className={styles.cardSubtitle}>Visibility score per topic</p>
      </div>
    </div>
    <div className={classNames(styles.gridHead, styles.topicGrid)}>
      <span>#</span>
      <span>Topic</span>
      <span>Visibility</span>
      <span className={styles.alignRight}>Mentions</span>
      <span className={styles.alignRight}>Trend</span>
    </div>
    {topics.length === 0 && (
      <EmptyRows text="Topics appear once your prompts have detected topic tags" />
    )}
    {topics.map((topic, index) => (
      <div
        className={classNames(styles.topicGrid, styles.lineRow)}
        key={topic.topic}
      >
        <span className={styles.muted}>{index + 1}</span>
        <span className={styles.topicName}>{topic.topic}</span>
        <span className={styles.barWithValue}>
          <MeterBar band={bandOf(topic.visibility)} value={topic.visibility} />
          <strong>{formatNumber(topic.visibility)}</strong>
        </span>
        <strong className={styles.alignRight}>
          {formatNumber(topic.mentions)}
        </strong>
        <span className={styles.alignRight}>
          <DeltaBadge kind="points" value={topic.trend} />
        </span>
      </div>
    ))}
    {topics.length > 0 && topics.length < 5 && (
      <p className={styles.topicsHint}>
        Prompts are grouped by their topic tag. Your prompts cover{' '}
        {topics.length === 1 ? '1 topic' : `${topics.length} topics`}
      </p>
    )}
  </section>
)

// ---------- Competitors & cited domains ----------

interface CompetitorsDomainsProps {
  brandProfileId: string
  competitors: BrandDashboard['competitors']
  domains: BrandDashboard['domains']
  // A competitor's dashboard: the subject row is that competitor, not "you".
  isCompetitorView: boolean
  ownedCitationShare: number
}

export const CompetitorsDomainsCard: FC<CompetitorsDomainsProps> = ({
  brandProfileId,
  competitors,
  domains,
  isCompetitorView,
  ownedCitationShare,
}) => {
  // Five rows on each side (QA, 2026-10-21): the subject plus the top four
  // other brands, next to the top five domains. Rows run by share of voice
  // and carry their real rank, the subject included (QA, 2026-10-21 17:45).
  const byVoice = [...competitors].sort(
    (a, b) => b.shareOfVoice - a.shareOfVoice
  )
  const rankOf = new Map(byVoice.map((row, index) => [row.key, index + 1]))
  const rows = competitors
    .slice(0, 5)
    .sort((a, b) => b.shareOfVoice - a.shareOfVoice)
  const maxDomainShare = Math.max(...domains.map((domain) => domain.share), 0)
  // Bars are relative to the loudest brand, so a 29 % leader reads as a full
  // bar rather than a sliver.
  const maxVoice = Math.max(
    ...rows.map((competitor) => competitor.shareOfVoice),
    0
  )

  return (
    <section className={classNames(styles.card, styles.sectionCard)}>
      <div>
        <h2 className={styles.sectionTitle}>Competitors &amp; Cited Domains</h2>
        <p className={styles.sectionSubtitle}>
          Share of voice and the sources behind your AI visibility
        </p>
      </div>

      <div className={styles.twoColumns}>
        <div className={styles.innerCard}>
          <div className={styles.cardHeader}>
            <div>
              <h3 className={styles.cardTitleSm}>Top 5 Competitors</h3>
              <p className={styles.cardSubtitle}>
                Share of voice across tracked prompts
              </p>
            </div>
          </div>
          <div className={classNames(styles.gridHead, styles.competitorGrid)}>
            <span>#</span>
            <span>Brand</span>
            <span>Share of voice</span>
            <span className={styles.alignRight}>Trend</span>
          </div>
          {rows.length === 0 && <EmptyRows />}
          <div className={styles.boxRows}>
            {rows.map((competitor) => {
              const isYou = competitor.label === 'you'
              return (
                <div
                  className={classNames(styles.competitorGrid, styles.boxRow, {
                    [styles.boxRowHighlight]: isYou,
                  })}
                  key={competitor.key}
                >
                  <span className={isYou ? styles.star : styles.muted}>
                    {rankOf.get(competitor.key)}
                  </span>
                  <span className={styles.brandCell}>
                    <span
                      className={classNames(styles.letterAvatar, {
                        [styles.letterAvatarYou]: isYou,
                      })}
                    >
                      {firstLetter(competitor.name)}
                    </span>
                    <span className={isYou ? styles.strong : undefined}>
                      {competitor.name}
                      {isYou && !isCompetitorView && ' (you)'}
                      {isCompetitorView &&
                        competitor.profileId === brandProfileId &&
                        ' (your brand)'}
                    </span>
                  </span>
                  <span className={styles.barWithValue}>
                    <MeterBar
                      color={isYou ? '#8080ff' : '#c7ced1'}
                      value={
                        maxVoice
                          ? (competitor.shareOfVoice / maxVoice) * 100
                          : 0
                      }
                    />
                    <strong>{formatPercent(competitor.shareOfVoice)}</strong>
                  </span>
                  <span className={styles.alignRight}>
                    {/* A rival gaining share is bad news for the subject. */}
                    <DeltaBadge
                      kind="points"
                      lowerIsBetter={!isYou}
                      value={competitor.trend}
                    />
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div className={styles.innerCard}>
          <div className={styles.cardHeader}>
            <div>
              <h3 className={styles.cardTitleSm}>Top 5 Cited Domains</h3>
              <p className={styles.cardSubtitle}>
                Sources AI platforms cite in answers to your prompts
              </p>
            </div>
            <Pill dot tone="purple">
              Owned {formatPercent(ownedCitationShare)}
            </Pill>
          </div>
          <div className={classNames(styles.gridHead, styles.domainGrid)}>
            <span>#</span>
            <span>Domain</span>
            <span>Type</span>
            <span>Share</span>
            <span className={styles.alignRight}>Citations</span>
          </div>
          {domains.length === 0 && <EmptyRows />}
          <div className={styles.boxRows}>
            {domains.map((domain, index) => (
              <div
                className={classNames(styles.domainGrid, styles.boxRow, {
                  [styles.boxRowHighlight]: domain.type === 'owned',
                })}
                key={domain.domain}
              >
                <span className={styles.muted}>{index + 1}</span>
                <span className={styles.brandCell}>
                  <span
                    className={classNames(
                      styles.letterAvatar,
                      styles[`letterAvatar_${domain.type}`]
                    )}
                  >
                    {firstLetter(domain.domain)}
                  </span>
                  <span className={styles.ellipsis}>{domain.domain}</span>
                </span>
                <span>
                  <Pill tone={CITATION_TYPE_TONES[domain.type]}>
                    {CITATION_TYPE_LABELS[domain.type]}
                  </Pill>
                </span>
                <span title={formatPercent(domain.share)}>
                  <MeterBar
                    color={TYPE_COLORS[domain.type]}
                    value={
                      maxDomainShare ? (domain.share / maxDomainShare) * 100 : 0
                    }
                  />
                </span>
                <strong className={styles.alignRight}>
                  {formatNumber(domain.citations)}
                </strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------- Cited pages ----------

interface CitedPagesCardProps {
  ownedPagesInTop: number
  pages: BrandDashboard['pages']
}

export const CitedPagesCard: FC<CitedPagesCardProps> = ({
  ownedPagesInTop,
  pages,
}) => (
  <section className={styles.card}>
    <div className={styles.cardHeader}>
      <div>
        <h2 className={styles.cardTitle}>Top 5 Cited Pages</h2>
        <p className={styles.cardSubtitle}>
          Exact URLs that AI answers link to
        </p>
      </div>
      {pages.length > 0 && (
        <Pill dot tone={ownedPagesInTop > 0 ? 'purple' : 'orange'}>
          {ownedPagesInTop} of {pages.length} are owned pages
        </Pill>
      )}
    </div>
    <div className={classNames(styles.gridHead, styles.pageGrid)}>
      <span>#</span>
      <span>Page</span>
      <span>Type</span>
      <span>Top platform</span>
      <span className={styles.alignRight}>Citations</span>
      <span className={styles.alignRight}>Trend</span>
    </div>
    {pages.length === 0 && <EmptyRows />}
    {pages.map((page, index) => (
      <div
        className={classNames(styles.pageGrid, styles.lineRow)}
        key={page.url}
      >
        <span className={styles.muted}>{index + 1}</span>
        <span className={styles.pageCell}>
          <span className={styles.pageTitle}>{page.title || page.domain}</span>
          <a
            className={styles.pageUrl}
            href={page.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {displayUrl(page.url)}
          </a>
        </span>
        <span>
          <Pill tone={CITATION_TYPE_TONES[page.type]}>
            {CITATION_TYPE_LABELS[page.type]}
          </Pill>
        </span>
        <span>{page.topPlatform ?? '—'}</span>
        <strong className={styles.alignRight}>
          {formatNumber(page.citations)}
        </strong>
        <span className={styles.alignRight}>
          <DeltaBadge kind="percent" value={page.trend} />
        </span>
      </div>
    ))}
  </section>
)
